import { useCallback, useEffect, useRef } from "react";

import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { getOrganizationId } from "Libs/utils";
import { RootState } from "Store/configureStore";
import { useAppDispatch } from "Store/hooks";

import {
  clearCarbonEmissionsData,
  setOrganizationLoading
} from "../carbonEmissions.reducer";
import {
  carbonEmissionsStateByOrganizationIdSelector,
  carbonEmissionsMonthlyDataForChartSelector,
  carbonEmissionsPreviousYearTotalSelector,
  carbonEmissionsCurrentYearTotalSelector
} from "../carbonEmissions.selectors";
import { fetchAllOrganizationCarbonEmissionsThunk } from "../thunks/fetchOrganizationCarbonEmissions.thunk";

export const useOrganizationCarbonEmissions = (organizationName?: string) => {
  const dispatch = useAppDispatch();
  const dataFetchedRef = useRef(false);
  const { organizationId: urlOrganizationName } = useParams<{
    organizationId: string;
  }>();
  const effectiveOrgName = organizationName || urlOrganizationName;

  // Get the actual organizationId
  const organizationId = useSelector((state: RootState) =>
    getOrganizationId(state, effectiveOrgName)
  );

  // Create stable references for values that change
  const refs = useRef({
    orgName: effectiveOrgName,
    orgId: organizationId
  });

  // Update refs when dependencies change
  useEffect(() => {
    refs.current = {
      orgName: effectiveOrgName,
      orgId: organizationId
    };
  }, [effectiveOrgName, organizationId]);

  // Select the data using the organizationId
  const state = useSelector((state: RootState) =>
    organizationId
      ? carbonEmissionsStateByOrganizationIdSelector(state, { organizationId })
      : undefined
  );

  const data = state?.data;
  const isLoading = state?.isLoading || false;
  const error = state?.error;

  const monthlyChartData = useSelector((state: RootState) =>
    organizationId
      ? carbonEmissionsMonthlyDataForChartSelector(state, { organizationId })
      : undefined
  );

  const previousYearTotal = useSelector((state: RootState) =>
    organizationId
      ? carbonEmissionsPreviousYearTotalSelector(state, { organizationId })
      : undefined
  );

  const currentYearTotal = useSelector((state: RootState) =>
    organizationId
      ? carbonEmissionsCurrentYearTotalSelector(state, { organizationId })
      : undefined
  );

  const loadData = useCallback(() => {
    const { orgName, orgId } = refs.current;

    if (orgName && orgId && !dataFetchedRef.current) {
      dataFetchedRef.current = true;

      // Set loading state before dispatching the thunk
      dispatch(
        setOrganizationLoading({
          organizationId: orgId,
          isLoading: true
        })
      );

      return dispatch(fetchAllOrganizationCarbonEmissionsThunk(orgName));
    }
  }, [dispatch]);

  // Clear data
  const clearData = useCallback(() => {
    const { orgId } = refs.current;
    if (orgId) {
      dispatch(clearCarbonEmissionsData({ organizationId: orgId }));
      dataFetchedRef.current = false;
    }
  }, [dispatch]);

  // Fixed missing dependency: Added organizationName to dependency array
  useEffect(() => {
    if (dataFetchedRef.current && !data) {
      dataFetchedRef.current = false;
    }

    if (
      !isLoading &&
      !data &&
      effectiveOrgName &&
      organizationId &&
      !dataFetchedRef.current
    ) {
      loadData();
    }

    return () => {
      if (effectiveOrgName !== organizationName) {
        dataFetchedRef.current = false;
      }
    };
  }, [
    effectiveOrgName,
    organizationId,
    data,
    isLoading,
    loadData,
    organizationName
  ]);

  return {
    data,
    isLoading,
    error,
    loadData,
    clearData,
    monthlyChartData,
    previousYearTotal,
    currentYearTotal
  };
};
